import { Component, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseComponent, environment } from '@eview/core';
import { GoogleGeocodingService } from '@eview/features/ui/map/google-geocoding.service';
import { CategoriesEnum } from '@eview/core/domain/post/post';
import { EGetAlertsActions, GetAlertsPost } from '@eview/core/store/actions/get-alerts.actions';
import { EMapActions, SetMapDefaults, SimulateUserClickedMap, UserClickedMap } from '@eview/core/store/actions/map.actions';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { ToastType } from '../toast/toast.component';
import { ToastService } from '../toast/toast.service';
import { GMapComponent } from '@eview/features/ui/map/gmap.component';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { executeRecaptcha } from '@eview/core/auth/recaptcha.helpers';
const MAP_ZOOM_CITY = 14;
const MAP_MAX_ZOOM = 20;

@Component({
  selector: 'eview-get-alerts',
  templateUrl: 'get-alerts.component.html',
  styleUrls: ['get-alerts.component.scss']
})
export class GetAlertsComponent extends BaseComponent
  implements OnInit, OnDestroy {
  private subs: Subscription;
  getAlertsForm: UntypedFormGroup;
  submitted = false;
  showDropDown = false;
  dropDownValues: any = [];
  email: string = '';
  tags$: Observable<any[]>;
  categories$: Observable<any[]>;

  constructor(
    private store: Store<AppState>, 
    private actions$: Actions, 
    private toast: ToastService, 
    private fb: UntypedFormBuilder, 
    private googleGeocodingService: GoogleGeocodingService,
    private recaptchaV3Service: ReCaptchaV3Service,
    @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef
  ) {
    super();
    this.subs = new Subscription();
    this.tags$ = this.store.select(selectTags).pipe(
      map((tags: any) => tags?.results ? tags.results : [])
    );
    this.categories$ = this.tags$.pipe(
      map((tagsData: any[]) => tagsData.filter((data: any) => data.type === CategoriesEnum.categories_List))
    );
    toast.setViewContainerRef(viewContainerRef);

  }
  @ViewChild(GMapComponent, { static: false }) mapComponent;
  rangeArray = ["1", "5", "10", "20", "50", "100"]


  ngOnInit() {
    this.store.dispatch(new UserClickedMap({ actual: null }));

    this.initForm();


    this.actions$
      .pipe(ofType(EGetAlertsActions.GetAlertsSuccess))
      .subscribe(() => {
        this.resetForm();
        this.initForm();
        if (this.mapComponent) {
          this.mapComponent.resetMap();
        }
        this.toast.show(ToastType.Success, 'ALERTS_SUCCESS');
      });

    this.subs.add(
      this.actions$
        .pipe(ofType<SetMapDefaults>(EMapActions.UserClickedMap))
        .subscribe(actions => {

          if (actions.payload && actions.payload.actual) {
            this.getAlertsForm.controls['latitude'].setValue(actions.payload.actual.lat.toString());
            this.getAlertsForm.controls['longitude'].setValue(actions.payload.actual.lon.toString());
            
            // Obtener dirección del punto clickeado
            this.googleGeocodingService.reverse(actions.payload.actual.lat, actions.payload.actual.lon).subscribe(data => {
              if (data && data.length > 0) {
                this.getAlertsForm.controls['location'].setValue(data[0].display_name);
              }
            });
            
            // Ajustar zoom y centrar en el punto clickeado
            if (this.mapComponent && this.mapComponent.map) {
              const currentRadius = parseInt(this.getAlertsForm.controls['radius'].value);
              let zoom;
              if (currentRadius <= 1) zoom = 14;
              else if (currentRadius <= 5) zoom = 12;
              else if (currentRadius <= 10) zoom = 11;
              else if (currentRadius <= 20) zoom = 10;
              else if (currentRadius <= 50) zoom = 8;
              else zoom = 7;
              
              this.mapComponent.map.setCenter({ lat: actions.payload.actual.lat, lng: actions.payload.actual.lon });
              this.mapComponent.map.setZoom(zoom);
            }
          }
        })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new UserClickedMap({ actual: null }));
  }

  sliderChange(index) {
    const radius = parseInt(this.rangeArray[index]);
    this.getAlertsForm.controls['radius'].setValue(this.rangeArray[index]);
    if (this.mapComponent) {
      this.mapComponent.updateRadiusCircle(radius);
      
      // Calcular zoom basado en el radio
      let zoom;
      if (radius <= 1) zoom = 14;
      else if (radius <= 5) zoom = 12;
      else if (radius <= 10) zoom = 11;
      else if (radius <= 20) zoom = 10;
      else if (radius <= 50) zoom = 8;
      else zoom = 7;
      
      this.mapComponent.map.setZoom(zoom);
    }
  }

  shareCheckedList(checkedCategoryList: any[]) {
    let categoryID = []
    if (checkedCategoryList.length) {
      checkedCategoryList.forEach(category => categoryID.push(category.id.toString()));
    }
    this.getAlertsForm.setControl('categories', this.fb.array(categoryID));
  }

  onLocationInput(event) {
    const allowedPattern = /^[a-zA-Z0-9.,\-\/\'"#°ªºáéíóúüñÁÉÍÓÚÜÑ()\s]*$/;
    let value = event.target.value;
    
    // Remover espacios al inicio
    if (value.startsWith(' ')) {
      value = value.trimStart();
      event.target.value = value;
      this.getAlertsForm.controls['location'].setValue(value);
    }
    
    // Validar caracteres permitidos
    if (!allowedPattern.test(value)) {
      const filteredValue = value.replace(/[^a-zA-Z0-9.,\-\/\'"#°ªºáéíóúüñÁÉÍÓÚÜÑ()\s]/g, '');
      event.target.value = filteredValue;
      this.getAlertsForm.controls['location'].setValue(filteredValue);
      value = filteredValue;
    }
    
    this.onLocationChange(value);
  }

  onLocationChange(value) {
    if(value.trim() !== '') {
      this.googleGeocodingService.forward(value).subscribe(data => {
        if (data && data.length) {
          this.dropDownValues = [];
          data.forEach(location => {
            const name = location.name || '';
            const address = location.address || location.display_name || '';
            let displayLabel;
            
            if (name && address && name !== address) {
              displayLabel = `${name} - ${address}`;
            } else {
              displayLabel = address || name;
            }
            
            this.dropDownValues.push({
              place_id: location.place_id,
              label: displayLabel,
              lat: location.lat,
              lon: location.lon
            });
          });
          this.showDropDown = true;
        } else {
          this.dropDownValues = [];
          this.showDropDown = false;
        }
      });
      this.store.dispatch(new UserClickedMap({ actual: null }));
    } else {
      this.dropDownValues = [];
      this.showDropDown = false;
    }
  }

  onEmailKeypress(event) {
    const char = String.fromCharCode(event.which);
    if (char === ' ') {
      event.preventDefault();
    }
  }

  onEmailInput(event) {
    const emailPattern = /^[a-zA-Z0-9._@-]*$/;
    let value = event.target.value.replace(/\s/g, '');
    
    if (!emailPattern.test(value)) {
      value = value.replace(/[^a-zA-Z0-9._@-]/g, '');
      event.target.value = value;
      this.getAlertsForm.controls['email'].setValue(value);
    }
    
    this.email = value;
  }

  trimEmail() {
    this.email = this.email.trim();
  }
  onSelectLocation(location) {
    this.getAlertsForm.controls['latitude'].setValue(location.lat.toString());
    this.getAlertsForm.controls['longitude'].setValue(location.lon.toString());
    this.getAlertsForm.controls['location'].setValue(location.label);
    this.onValueSelected([{lat:location.lat, lon:location.lon, type:'' }])
    this.showDropDown = false;
  }

  onValueSelected(result) {
    const position = result && result[0] ? result[0] : [];
    if (position && this.mapComponent && this.mapComponent.map) {
      const currentRadius = parseInt(this.getAlertsForm.controls['radius'].value);
      let zoom;
      if (currentRadius <= 1) zoom = 14;
      else if (currentRadius <= 5) zoom = 12;
      else if (currentRadius <= 10) zoom = 11;
      else if (currentRadius <= 20) zoom = 10;
      else if (currentRadius <= 50) zoom = 8;
      else zoom = 7;
      
      this.mapComponent.map.setCenter({ lat: position.lat, lng: position.lon });
      this.mapComponent.map.setZoom(zoom);
    }
    setTimeout(() => {
      this.store.dispatch(new SimulateUserClickedMap({ actual: position }));
      // Actualizar el círculo de rango después de posicionar el marcador
      if (this.mapComponent) {
        const currentRadius = this.getAlertsForm.controls['radius'].value;
        this.mapComponent.updateRadiusCircle(parseInt(currentRadius));
      }
    }, 100);
  }

  onSaveNotification() {
    this.submitted = true;
    if (this.getAlertsForm.valid && this.getAlertsForm.controls['categories'].value.length) {
      executeRecaptcha(this.recaptchaV3Service).subscribe((token) => {
        this.store.dispatch(
          new GetAlertsPost({
            ...this.getAlertsForm.value,
            token: token
          })
        );
      })
    }
  }

  initForm() {
    this.getAlertsForm = this.fb.group({
      location: new UntypedFormControl('', [Validators.required]),
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      radius: new UntypedFormControl('', [Validators.required]),
      categories: new UntypedFormArray([]),
      latitude: new UntypedFormControl('', [Validators.required, Validators.maxLength(255)]),
      longitude: new UntypedFormControl('', [Validators.required, Validators.maxLength(255)]),
    });

    this.getAlertsForm.controls['radius'].setValue(this.rangeArray[0]);
  }

  resetForm() {
    this.getAlertsForm.reset();
    this.getAlertsForm.setControl('categories', this.fb.array([]));
    Object.keys(this.getAlertsForm.controls).forEach(key => {
      this.getAlertsForm.get(key).setErrors(null);
    });
    this.store.dispatch(new UserClickedMap({ actual: null }));
    this.submitted = false
  }

  get control() { return this.getAlertsForm.controls; }

}
